// ============================================
// useTweenedNumbers — nội suy (tween) một mảng số bằng requestAnimationFrame
// ============================================

import { useEffect, useRef, useState } from 'react';

/** Thời gian chuyển động mặc định (ms). */
const DEFAULT_DURATION_MS = 600;

/** ease-out cubic: chạy nhanh lúc đầu, hãm dần về cuối. */
function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Người dùng bật "giảm chuyển động" ở hệ điều hành?
 * Có kiểm tra `window` / `matchMedia` để không vỡ khi chạy SSR hoặc trong test
 * (jsdom cũ không cài sẵn matchMedia).
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Số hợp lệ hay không — chặn NaN/Infinity lọt vào vòng nội suy. */
function safe(n: number | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/** So sánh hai mảng theo GIÁ TRỊ (không theo tham chiếu). */
function sameValues(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Nội suy mượt một mảng số từ giá trị ĐANG HIỂN THỊ sang giá trị đích.
 *
 * VÌ SAO TWEEN SỐ THAY VÌ CSS TRANSITION / CSS ANIMATION?
 * Donut được dựng bằng cách cộng dồn phần trăm: lát thứ i bắt đầu ở tổng của
 * các lát trước nó. Nghĩa là khi MỘT lát đổi giá trị thì cả GÓC BẮT ĐẦU của
 * mọi lát phía sau cũng đổi theo. Làm bằng CSS ta chỉ có thể transition từng
 * thuộc tính của từng thẻ (stroke-dashoffset, rotate...) một cách độc lập —
 * góc bắt đầu và độ rộng chạy lệch pha nhau nên giữa chừng các lát hở/đè lên
 * nhau. Ngoài ra CSS animation muốn chạy lại thì phải remount (key={...}), mà
 * remount = vẽ lại từ 0 — đúng thứ cần tránh khi đổi filter lúc đã có dữ liệu.
 *
 * Tween thẳng chính các con số rồi để component vẽ lại arc từ số đã nội suy ở
 * mỗi frame thì góc bắt đầu, độ rộng và nhãn % luôn nhất quán tuyệt đối, và
 * một cơ chế duy nhất lo được cả hai tình huống:
 *  - Lần đầu vào trang: giá trị hiển thị khởi tạo là mảng 0 -> biểu đồ fill từ 0.
 *  - Đổi filter khi đã có dữ liệu: điểm bắt đầu là giá trị ĐANG hiển thị ngay
 *    lúc đó -> biểu đồ morph sang giá trị mới, không giật về 0. Bấm đổi liên
 *    tục cũng chạy tiếp từ chỗ đang dở.
 *
 * @param target  Mảng giá trị đích (so sánh theo GIÁ TRỊ nên component render
 *                lại tạo mảng mới cũng không kích hoạt tween thừa).
 * @param durationMs Thời gian chuyển động, mặc định ~600ms.
 * @returns Mảng đã nội suy, độ dài LUÔN bằng `target.length`.
 */
export function useTweenedNumbers(target: number[], durationMs: number = DEFAULT_DURATION_MS): number[] {
  // Đọc một lần khi mount là đủ: đây là tuỳ chọn hệ thống, đổi giữa phiên rất hiếm.
  const [reduced] = useState(prefersReducedMotion);

  // Khởi tạo: reduce-motion -> đứng luôn ở đích; ngược lại bắt đầu từ 0 để lần
  // đầu vào trang biểu đồ vẫn fill dần lên.
  const [values, setValues] = useState<number[]>(() => (reduced ? target.map(safe) : target.map(() => 0)));

  // Giá trị ĐANG hiển thị (cập nhật mỗi frame) — điểm xuất phát cho tween kế tiếp.
  const displayRef = useRef<number[]>(values);
  // Đích của lần tween gần nhất, dùng để so sánh theo giá trị.
  const targetRef = useRef<number[] | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Cùng giá trị -> không làm gì. Nếu so theo tham chiếu thì mỗi lần cha
    // render lại (mảng mới) sẽ khởi động lại tween và biểu đồ rung liên tục.
    if (targetRef.current !== null && sameValues(targetRef.current, target)) return;
    targetRef.current = target;

    const to = target.map(safe);

    if (reduced || durationMs <= 0) {
      displayRef.current = to;
      setValues(to);
      return;
    }

    // Đang chạy dở mà có đích mới -> huỷ frame cũ, KHÔNG reset giá trị.
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

    const from = displayRef.current;
    // Độ dài có thể khác nhau (mỗi kỳ có số danh mục khác nhau): nội suy trên
    // phần dài nhất, chỗ thiếu coi như 0. Nhờ vậy lát sắp biến mất co dần về 0
    // và lát mới xuất hiện nở ra từ 0, thay vì nhảy cóc.
    const len = Math.max(from.length, to.length);
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = Math.max(0, now - start);
      const t = Math.min(1, elapsed / durationMs);
      const e = easeOutCubic(t);

      const frame = new Array<number>(len);
      for (let i = 0; i < len; i++) {
        const a = safe(from[i]);
        const b = safe(to[i]);
        frame[i] = a + (b - a) * e;
      }

      displayRef.current = frame;
      setValues(frame);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        // Chốt đúng giá trị đích (và đúng độ dài) để không đọng sai số float
        // hay phần dư của mảng dài hơn.
        displayRef.current = to;
        setValues(to);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    // `target` là mảng mới mỗi lần render nhưng thân effect đã tự chặn bằng
    // sameValues nên không có vòng lặp. KHÔNG cleanup huỷ rAF ở đây — effect
    // chạy lại sau mỗi frame, huỷ ở đây là tween chết ngay frame đầu.
  }, [target, durationMs, reduced]);

  // Chỉ huỷ khi unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  // Trả về đúng độ dài `target.length`:
  //  - Phần DƯ (mảng đang chạy dài hơn target, tức là các lát sắp biến mất)
  //    được cắt bỏ khỏi kết quả nhưng vẫn nằm trong `displayRef`, nên nếu người
  //    dùng đổi filter ngược lại giữa chừng thì chúng chạy tiếp từ giá trị dở
  //    dang chứ không nhảy từ 0.
  //  - Phần THIẾU (target vừa dài ra, frame đầu tiên chưa kịp chạy) trả 0 thay
  //    vì undefined — tránh NaN lọt vào công thức vẽ arc.
  const out = new Array<number>(target.length);
  for (let i = 0; i < target.length; i++) out[i] = safe(values[i]);
  return out;
}
